import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { APP_BUILD_VERSION } from '../generated/appBuildVersion';
import { ScaleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Input from '../components/Input';
import Button from '../components/Button';
import { authAPI } from '../services/api';
import { DEMO_TRAINING_PASSWORD, DEMO_TRAINING_SECTIONS } from '../config/demoApplicationUsers';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [loginError, setLoginError] = useState(null);
  const [maintenanceOn, setMaintenanceOn] = useState(false);
  const [maintenanceLoaded, setMaintenanceLoaded] = useState(false);

  const { user, login, loading } = useAuth();
  const { t } = useTranslation();
  const toast = useToast();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      authAPI
        .getMaintenanceStatus()
        .then((data) => {
          if (!cancelled) setMaintenanceOn(Boolean(data?.maintenance));
        })
        .catch(() => {
          if (!cancelled) setMaintenanceOn(false);
        })
        .finally(() => {
          if (!cancelled) setMaintenanceLoaded(true);
        });
    };
    load();
    const intervalId = setInterval(() => {
      if (!cancelled) load();
    }, 20000);
    const onVisibility = () => {
      if (!cancelled && document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const from = location.state?.from?.pathname || '/dashboard';

  // Redirect if already logged in
  if (user) {
    return <Navigate to={from} replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear login error when user starts typing
    if (loginError) {
      setLoginError(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = t('login.usernameRequired');
    }
    
    if (!formData.password) {
      newErrors.password = t('login.passwordRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const result = await login(formData);
    
    if (result.success) {
      toast.success(t('login.loginSuccess'));
    } else {
      // Show error message inline
      const message =
        result.code === 'PENDING_APPROVAL'
          ? t('login.pendingApproval')
          : result.code === 'MAINTENANCE_MODE'
            ? t('login.maintenanceLoginDenied')
            : result.error;
      setLoginError(message);
      // On login failure, clear only the password field for security
      // but retain the username for user convenience
      setFormData(prev => ({
        ...prev,
        password: ''
      }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {maintenanceLoaded && maintenanceOn ? (
        <div
          role="alert"
          className="w-full bg-amber-600 text-white px-4 py-2.5 text-center text-sm font-semibold shadow-md shrink-0"
        >
          {t('login.maintenanceTopBar')}
        </div>
      ) : null}
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
              <ScaleIcon className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {t('login.title')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {t('login.subtitle')}
            </p>
            {maintenanceLoaded && maintenanceOn ? (
              <div
                className="mt-4 flex gap-3 p-4 rounded-lg border-2 border-amber-300 bg-amber-50 text-amber-950 shadow-sm"
                role="status"
              >
                <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-left space-y-1">
                  <p className="font-semibold">{t('login.maintenanceHeading')}</p>
                  <p>{t('login.maintenanceBanner')}</p>
                </div>
              </div>
            ) : null}
          </div>
          
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <Input
                label={t('login.username')}
                name="username"
                type="text"
                value={formData.username}
                onChange={handleChange}
                error={errors.username}
                placeholder={t('login.usernamePlaceholder')}
                required
                autoComplete="username"
              />
              
              <Input
                label={t('login.password')}
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                error={errors.password}
                placeholder={t('login.passwordPlaceholder')}
                required
                autoComplete="current-password"
              />
            </div>

            {loginError && (
              <div className="flex gap-3 p-3 rounded-lg border border-red-200 bg-red-50 text-red-800" role="alert">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm">{loginError}</p>
              </div>
            )}

            <div>
              <Button
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                loading={loading}
                disabled={loading}
              >
                {t('login.signIn')}
              </Button>
              {maintenanceLoaded && maintenanceOn ? (
                <p className="mt-2 text-center text-sm text-amber-900 font-medium">
                  {t('login.maintenanceSignInHint')}
                </p>
              ) : null}
            </div>
            
            <div className="text-center">
              <p className="text-sm text-gray-600">{t('login.demoCredentials')}</p>
              <details className="mt-3 text-left rounded-lg border border-gray-200 bg-white shadow-sm">
                <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-blue-700 hover:bg-gray-50 rounded-lg">
                  {t('login.demoAccountsToggle')}
                </summary>
                <div className="px-3 pb-3 pt-1 border-t border-gray-100 max-h-64 overflow-y-auto">
                  <p className="text-xs text-gray-600 mt-2">
                    {t('login.demoPasswordLine', { password: DEMO_TRAINING_PASSWORD })}
                  </p>
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 mt-2">
                    {t('login.demoProductionWarning')}
                  </p>
                  {DEMO_TRAINING_SECTIONS.map((section) => (
                    <div key={section.categoryKey} className="mt-3">
                      <div className="text-xs font-semibold text-gray-800 uppercase tracking-wide">
                        {t(section.categoryKey)}
                      </div>
                      <ul className="mt-1 space-y-1 text-xs text-gray-700">
                        {section.accounts.map((row) => (
                          <li key={row.username} className="font-mono pl-1">
                            <span className="text-gray-900">{row.username}</span>
                            {row.email ? (
                              <span className="text-gray-500 font-sans">
                                {' '}
                                · {t('login.demoEmailLabel')}: {row.email}
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          </form>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4">
        <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-x-5 gap-y-1 text-sm text-gray-500">
          <span className="font-mono text-xs text-gray-400 tabular-nums" title={t('common.buildVersionTitle')}>
            {APP_BUILD_VERSION}
          </span>
          <span className="text-center">
            © {new Date().getFullYear()} Toubanetwork - Tous droits réservés
          </span>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;